import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  InGameCurrency,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton/index.tsx';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BodyL, BrandL } from '@sorare/core/src/atoms/typography/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import { mergeRewardsConfig } from '@sorare/core/src/lib/rewards.tsx';
import { useGetCardSetConfig } from '@sorare/core/src/set/index.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';
import { DualRewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/DualRewardUnlockedStory/index.tsx';
import { DumbRewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/DumbRewardUnlockedStory.tsx';
import { ButtonsWrapper } from '#src/components/missions/stories/RewardUnlockedStory/styles.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';
import type { ClaimWelcomeBackRewards_currentUser } from './__generated__/index.graphql.ts';

const Content = styled(Horizontal).attrs({ center: true })`
  position: relative;
  padding: 0 var(--double-unit) calc(10 * var(--unit));
  height: 100%;

  @media ${laptopAndAbove} {
    min-height: min(
      calc(90 * var(--unit)),
      calc(var(--100vh) - 8 * var(--unit))
    );
  }
`;
const Logo = styled.img`
  height: 48px;
  object-fit: contain;
`;

type Props = { currentUser: ClaimWelcomeBackRewards_currentUser };
export const ClaimWelcomeBackRewards = ({ currentUser }: Props) => {
  const { setEndOfSeasonTasks } = currentUser;
  const hasUnclaimedRewardTasks = setEndOfSeasonTasks.some(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );

  const getCardSetConfig = useGetCardSetConfig();
  const { sport } = useSportContext();

  const [open, setOpen] = useState(hasUnclaimedRewardTasks);
  const [loading, setLoading] = useState(false);
  const [showRewardDialog, setShowRewardDialog] = useState(false);

  const setConfig = getCardSetConfig();
  const { logoImage } = setConfig.visual.brand;
  const [claimTask] = useClaimTask();

  const mergedRewardConfigs = mergeRewardsConfig(
    setEndOfSeasonTasks.flatMap(task => task.rewardConfigs)
  );

  const inGameCurrencyRewardConfig = mergedRewardConfigs.find(
    isTypeFn('InGameCurrencyRewardConfig')
  );
  const gemRewardConfig =
    !!inGameCurrencyRewardConfig &&
    inGameCurrencyRewardConfig.currency === InGameCurrency.COMMON_GEM
      ? inGameCurrencyRewardConfig
      : undefined;
  const essenceRewardConfig = mergedRewardConfigs.find(
    isTypeFn('CardShardRewardConfig')
  );

  const showDualReward = !!gemRewardConfig && !!essenceRewardConfig;
  const showSingleReward = !!essenceRewardConfig && !showDualReward;

  const essenceTitle = (
    <BrandL>
      <FormattedMessage defaultMessage="Stellar Essence" />
    </BrandL>
  );
  const essenceSubtitle = (
    <BodyL>
      <FormattedMessage defaultMessage="Here is your reward to start playing the new set!" />
    </BodyL>
  );
  const gemTitle = (
    <BrandL>
      <FormattedMessage defaultMessage="Gems" />
    </BrandL>
  );
  const gemSubtitle = (
    <BodyL>
      <FormattedMessage defaultMessage="Use these gems to build your first lineup!" />
    </BodyL>
  );

  const claimAllTasks = async () => {
    setLoading(true);

    try {
      await Promise.all(
        setEndOfSeasonTasks.map(async task =>
          claimTask({
            variables: {
              taskId: task.id,
              sport,
            },
          })
        )
      );
      setLoading(false);
      setShowRewardDialog(true);
      if (!mergedRewardConfigs.length) {
        setOpen(false);
      }
    } catch {
      setLoading(false);
      setOpen(false);
    }
  };
  const onClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      style={{ background: setConfig.visual.welcomeBackDialog?.background }}
    >
      {showRewardDialog ? (
        <>
          {!!showSingleReward && (
            <DumbRewardUnlockedStory
              rewardConfigs={[essenceRewardConfig]}
              amount={essenceRewardConfig.quantity.toString()}
              title={essenceTitle}
              subtitle={essenceSubtitle}
              onClose={onClose}
              showContinueButton={false}
              hideBackground
              action={
                <Button size="large" color="primary" onClick={onClose}>
                  <FormattedMessage {...glossary.continue} />
                </Button>
              }
            />
          )}
          {!!showDualReward && (
            <DualRewardUnlockedStory
              onClose={onClose}
              hideBackground
              group1Props={{
                title: essenceTitle,
                subtitle: essenceSubtitle,
                rewardConfigs: [essenceRewardConfig],
                amount: essenceRewardConfig.quantity.toString(),
              }}
              group2Props={{
                title: gemTitle,
                subtitle: gemSubtitle,
                rewardConfigs: [gemRewardConfig],
                amount: gemRewardConfig.amount.toString(),
                action: (
                  <Button size="large" color="primary" onClick={onClose}>
                    <FormattedMessage {...glossary.continue} />
                  </Button>
                ),
              }}
              showContinueButton={false}
            />
          )}
        </>
      ) : (
        <Content>
          <Vertical center gap={5} className="text-center">
            {!!logoImage && <Logo src={logoImage} alt="" />}
            <Vertical center gap={2}>
              <BrandL>
                <FormattedMessage
                  defaultMessage="New season.{br}New galaxy."
                  values={{
                    br: <br />,
                  }}
                />
              </BrandL>
              <BodyL>
                <FormattedMessage defaultMessage="Your performance last season has earned you a head start in the Stellar universe." />
              </BodyL>
            </Vertical>
          </Vertical>
          <ButtonsWrapper>
            <LoadingButton
              size="large"
              color="primary"
              loading={loading}
              onClick={claimAllTasks}
            >
              <FormattedMessage defaultMessage="Claim my rewards" />
            </LoadingButton>
          </ButtonsWrapper>
        </Content>
      )}
    </Dialog>
  );
};

ClaimWelcomeBackRewards.fragments = {
  currentUser: gql`
    fragment ClaimWelcomeBackRewards_currentUser on CurrentUser {
      slug
      setEndOfSeasonTasks(sport: $sport) {
        id
        aasmState
        rewardConfigs {
          id
          ...DualRewardUnlockedStory_rewardConfig
          ...mergeRewardsConfig_anyRewardConfigInterface
          ...DumbRewardUnlockedStory_rewardConfig
        }
      }
    }
    ${DualRewardUnlockedStory.fragments.rewardConfig}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
    ${DumbRewardUnlockedStory.fragments.rewardConfig}
  ` as TypedDocumentNode<ClaimWelcomeBackRewards_currentUser>,
};
